/**
 * Content Slice — MangoTV
 * Pattern: mirrors glotv moviesSlice.js
 *
 * Manages all content state: VOD categories, movie details,
 * channels (live TV), search results, video playback URLs.
 *
 * Categories are dynamic — they come from the API and get
 * spread into state as individual keys (e.g., state.trending = [...]).
 */

import { createSlice } from "@reduxjs/toolkit";

const initialState = {
  // === VOD Content ===
  categories: [], // raw category list from API [{id, uid, name, ...}]
  categoryMap: {}, // uid -> id mapping
  categoryIdToUidMap: {}, // id -> uid mapping (for home page filtering)
  moviesByCategories: {}, // { categoryUid: [movies] }

  // === Live TV ===
  channels: [],
  channelCategories: [],

  // === Movie / Series Details ===
  movieDetails: null,
  seriesInfo: null,
  episodes: [], // episodes for a series season

  // === Similar / Related ===
  similarMovies: [],

  // === Search ===
  searchQuery: "",
  searchResults: [],

  // === Video Playback ===
  videoUrl: null, // current playback URL object

  // === Banners ===
  banners: [],

  // === TV Store Categories ===
  tvStoreCategories: [],

  // === Podcasts ===
  podcasts: [],

  // === Live Movies (movie_type === "live" — drives Upcoming/Past tabs on LiveShowPage) ===
  liveMovies: [],

  // === Watchlist ===
  watchlist: [],

  // === User Packages ===
  packages: [],
  packageIds: "",

  // === Loading States ===
  loaders: {
    content: true,
    movieDetails: true,
    channels: true,
    search: false,
    video: true,
    watchlist: true,
    similarMovies: true,
    banners: true,
    tvStoreCategories: true,
    podcasts: true,
    liveMovies: true,
  },

  // === Error States ===
  errors: {
    content: null,
    movieDetails: null,
    channels: null,
    search: null,
    video: null,
  },
};

const contentSlice = createSlice({
  name: "content",
  initialState,
  reducers: {
    // === VOD Content ===
    fetchContentBegin: (state) => {
      state.loaders.content = true;
      state.errors.content = null;
    },
    fetchContentSuccess: (state, action) => {
      const {
        categories,
        categoryMap,
        categoryIdToUidMap,
        moviesByCategories,
        packages,
        packageIds,
        ...dynamicCategories
      } = action.payload;

      state.categories = categories || state.categories;
      state.categoryMap = categoryMap || state.categoryMap;
      state.categoryIdToUidMap = categoryIdToUidMap || state.categoryIdToUidMap;
      state.moviesByCategories = moviesByCategories || state.moviesByCategories;
      state.packages = packages || state.packages;
      state.packageIds = packageIds || state.packageIds;

      // Spread dynamic categories into state (e.g., state.trending = [...])
      Object.entries(dynamicCategories).forEach(([key, value]) => {
        state[key] = value;
      });

      state.loaders.content = false;
    },
    fetchContentError: (state, action) => {
      state.loaders.content = false;
      state.errors.content = action.payload || "Failed to load content";
    },

    // === Movie Details ===
    fetchMovieDetailsBegin: (state) => {
      state.loaders.movieDetails = true;
      state.errors.movieDetails = null;
      state.movieDetails = null;
    },
    fetchMovieDetailsSuccess: (state, action) => {
      state.movieDetails = action.payload;
      state.loaders.movieDetails = false;
    },
    fetchMovieDetailsError: (state, action) => {
      state.loaders.movieDetails = false;
      state.errors.movieDetails = action.payload || "Failed to load details";
    },

    // === Series Info ===
    setSeriesInfo: (state, action) => {
      state.seriesInfo = action.payload;
    },
    setEpisodes: (state, action) => {
      state.episodes = action.payload;
    },

    // === Similar Movies ===
    setSimilarMovies: (state, action) => {
      state.similarMovies = action.payload;
      state.loaders.similarMovies = false;
    },

    // === Live TV Channels ===
    fetchChannelsBegin: (state) => {
      state.loaders.channels = true;
      state.errors.channels = null;
    },
    fetchChannelsSuccess: (state, action) => {
      state.channels = action.payload;
      state.loaders.channels = false;
    },
    fetchChannelsError: (state, action) => {
      state.loaders.channels = false;
      state.errors.channels = action.payload || "Failed to load channels";
    },
    setChannelCategories: (state, action) => {
      state.channelCategories = action.payload;
    },

    // === Search ===
    setSearchBegin: (state) => {
      state.loaders.search = true;
      state.errors.search = null;
    },
    setSearchResults: (state, action) => {
      state.searchQuery = action.payload.query;
      state.searchResults = action.payload.results;
      state.loaders.search = false;
    },
    setSearchError: (state, action) => {
      state.loaders.search = false;
      state.errors.search = action.payload || "Search failed";
    },
    clearSearch: (state) => {
      state.searchQuery = "";
      state.searchResults = [];
      state.loaders.search = false;
    },

    // === Video Playback ===
    fetchVideoBegin: (state) => {
      state.loaders.video = true;
      state.errors.video = null;
      state.videoUrl = null;
    },
    fetchVideoSuccess: (state, action) => {
      state.videoUrl = action.payload;
      state.loaders.video = false;
    },
    fetchVideoError: (state, action) => {
      state.loaders.video = false;
      state.errors.video = action.payload || "Failed to load video";
    },

    // === Banners ===
    setBanners: (state, action) => {
      state.banners = action.payload;
      state.loaders.banners = false;
    },

    // === TV Store Categories ===
    setTvStoreCategories: (state, action) => {
      state.tvStoreCategories = action.payload;
      state.loaders.tvStoreCategories = false;
    },

    // === Podcasts ===
    setPodcasts: (state, action) => {
      state.podcasts = action.payload;
      state.loaders.podcasts = false;
    },

    // === Live Movies ===
    setLiveMovies: (state, action) => {
      state.liveMovies = action.payload;
      state.loaders.liveMovies = false;
    },
    fetchLiveMoviesBegin: (state) => {
      state.loaders.liveMovies = true;
    },

    // === Watchlist ===
    setWatchlist: (state, action) => {
      state.watchlist = action.payload;
      state.loaders.watchlist = false;
    },

    // === Reset ===
    resetContent: () => initialState,
  },
});

export const {
  fetchContentBegin,
  fetchContentSuccess,
  fetchContentError,
  fetchMovieDetailsBegin,
  fetchMovieDetailsSuccess,
  fetchMovieDetailsError,
  setSeriesInfo,
  setEpisodes,
  setSimilarMovies,
  fetchChannelsBegin,
  fetchChannelsSuccess,
  fetchChannelsError,
  setChannelCategories,
  setSearchBegin,
  setSearchResults,
  setSearchError,
  clearSearch,
  fetchVideoBegin,
  fetchVideoSuccess,
  fetchVideoError,
  setBanners,
  setTvStoreCategories,
  setPodcasts,
  setLiveMovies,
  fetchLiveMoviesBegin,
  setWatchlist,
  resetContent,
} = contentSlice.actions;

export default contentSlice.reducer;
